import get from "lodash/get";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {PlanInfo, PlanLimit} from "@/lib/types/accounts.ts";
import {BillingPeriod, Plan} from "@/lib/types/billing.ts";
import {MonitoringServiceType} from "@/lib/types/checks.ts";
import {PopupAddOns} from "@/pages/billing/v1/instantCheckout/PopupAddOns.tsx";
import {PopupAddOnsRequest} from "@/pages/billing/v1/instantCheckout/PopupAddOnsRequest.tsx";
import {PopupContactSupport} from "@/pages/billing/v1/instantCheckout/PopupContactSupport.tsx";
import {PopupRequestUpgradePlan} from "@/pages/billing/v1/instantCheckout/PopupRequestUpgradePlan.tsx";
import {PopupUpgradePlan} from "@/pages/billing/v1/instantCheckout/PopupUpgradePlan.tsx";

interface PopupContentProps {
  show: boolean;
  onClose: () => void;
  service: MonitoringServiceType;
  currentPlan: PlanInfo;
  nextPlan?: PlanInfo;
  limitName: string;
  requestedAt?: string;
}

export const PopupContent = ({
  show,
  onClose,
  service,
  nextPlan,
  currentPlan,
  limitName,
  requestedAt,
}: PopupContentProps) => {
  const {user} = useCurrentUserContext();
  const planLimit = get(currentPlan.limits, limitName) as PlanLimit;
  const canPurchase = user?.has_admin_access || user?.has_purchase_permission;
  const canUpgrade = nextPlan && currentPlan.name !== nextPlan.name;
  const isLimitExhausted = planLimit.limit > planLimit.used;
  const period = user?.account.billing_period;
  if (!period) {
    console.error("Billing period is not defined for the user account:", user?.email);
    return null;
  }

  if (!nextPlan || currentPlan.name === Plan.Custom) {
    return <PopupContactSupport show={show} service={service} onComplete={onClose} amount={planLimit.bundleSize} />;
  }

  if (!isLimitExhausted && canUpgrade && canPurchase) {
    return (
      <PopupUpgradePlan
        show={show}
        service={service}
        onComplete={onClose}
        plan={nextPlan.name}
        price={period === BillingPeriod.MONTH ? nextPlan.price_monthly : nextPlan.price_annual}
        period={period}
        planLimit={planLimit}
      />
    );
  }

  if (!isLimitExhausted && canUpgrade && canPurchase) {
    return (
      <PopupRequestUpgradePlan
        show={show}
        service={service}
        onComplete={onClose}
        plan={nextPlan.name}
        price={period === BillingPeriod.MONTH ? nextPlan.price_monthly : nextPlan.price_annual}
        period={period}
        limitField={limitName}
        planLimit={planLimit}
        requestedAt={requestedAt}
      />
    );
  }

  if (canPurchase) {
    return (
      <PopupAddOns
        show={show}
        service={service}
        onComplete={onClose}
        period={period}
        limitField={limitName}
        amount={planLimit.bundleSize}
        maxLimit={planLimit.limit}
        accountLimit={planLimit.allowed}
      />
    );
  }

  return (
    <PopupAddOnsRequest
      show={show}
      service={service}
      onComplete={onClose}
      amount={planLimit.bundleSize}
      limitField={limitName}
      accountLimit={planLimit.allowed}
      period={period}
      requestedAt={requestedAt}
    />
  );
};
